"use client";

import {JSX, memo, ReactNode, useRef} from "react";

import {MaxLinesConfig, MaxLinesValue, SSREstimation, useExpandableText} from "@/shared/ui/data-display/expandable-text/use-expandable-text";

import "./styles/expandable-text.scss";

interface ExpandableTextProps {
    children: ReactNode;
    /**
     * Maximum lines to show when collapsed.
     * Can be a value (0, 3, 4, 6, 8, 10, 12, 14, 16) or config per device type.
     * @example maxLines={6}
     * @example maxLines={{ mobile: 4, tablet: 6, desktop: 8 }}
     */
    maxLines?: MaxLinesValue | MaxLinesConfig;
    className?: string;
    /** Character count of the content for SSR prediction */
    charCount?: number;
    /**
     * SSR estimation config per device type
     * @example
     * ssrEstimation={{
     *   mobile: { width: 320, fontSize: 14 },
     *   tablet: { width: 600 },
     *   desktop: { width: 800 }
     * }}
     */
    ssrEstimation?: SSREstimation;
}

/**
 * ExpandableText component with smooth expand/collapse animation
 *
 * @example
 * ```tsx
 * <ExpandableText
 *   maxLines={{ mobile: 4, desktop: 6 }}
 *   charCount={text.length}
 * >
 *   <p>{text}</p>
 * </ExpandableText>
 * ```
 */
export const ExpandableText = memo(function ExpandableText({
    children,
    maxLines,
    className = "",
    charCount,
    ssrEstimation,
}: ExpandableTextProps): JSX.Element {
    const containerRef = useRef<HTMLDivElement | null>(null);

    const expandable = useExpandableText({
        containerRef,
        charCount: charCount ?? 0,
        maxLines,
        ssrEstimation,
    });

    return (
        <div
            ref={containerRef}
            className={`expandable-text expandable-text--${expandable.maxLines}-lines ${!expandable.isReady ? "expandable-text--measuring" : ""} ${expandable.showEllipsis && expandable.canExpand ? "expandable-text--ellipsis" : ""} ${className}`}
            style={{
                maxHeight: expandable.isReady
                    ? expandable.isExpanded
                        ? `${expandable.expandedHeight}px`
                        : `${expandable.collapsedHeight}px`
                    : undefined,
            }}
        >
            {children}
        </div>
    );
});
